import {
  Card,
  CardBody,
  Row,
  Col,
  Breadcrumb,
  BreadcrumbItem,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import ReactTable from "components/ReactTable/ReactTable.js";
import Moment from "moment";
import "moment-timezone";
import { useMemo, useState, React, useEffect, useContext } from "react";
import { NavLink, useHistory, useLocation } from "react-router-dom";
import Select from "react-select";
import LoadingOverlay from "react-loading-overlay";
LoadingOverlay.propTypes = undefined;
import { ToastContainer, toast } from "react-toastify";
import ReactBSAlert from "react-bootstrap-sweetalert";
import { MainContext } from "context/MainContext.js";
import { faHome } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Utilities from "components/Utils/utilities";

const Auditing = () => {
  const history = useHistory();
  const location = useLocation();

  const [forceRefreshCount, setForceRefreshCount] = useState(0);
  const [showLoading, setShowLoading] = useState(false);
  const [alert, setAlert] = useState(null);
  const privileges = useContext(MainContext);
  const [params, setParams] = useState(location.state?.value ?? "");
  const filterButtonsOptions = [
    { value: "type||all", label: "All Types", key: "all" },
    { value: "type||navigation", label: "Navigation Types", key: "navigation" },
    { value: "type||create", label: "Create Types", key: "create" },
    { value: "type||update", label: "Update Types", key: "update" },
    { value: "type||delete", label: "Delete Types", key: "delete" },
    { value: "type||device", label: "Device Types", key: "device" },
  ];

  useEffect(() => {
    if (!Utilities.hasAccess(privileges["admin.auditing.display"])) {
      history.push({
        pathname: "/",
      });
    } else if (Utilities.hasAgreedTerms() == "false") {
      history.push({
        pathname: "/auth/terms",
      });
    }
  }, []);

  const hideAlert = () => {
    setAlert(null);
  };

  const filterButtons = useMemo(() => {
    let defaultValue = filterButtonsOptions.find((c) => c.value == params);
    if (defaultValue == undefined) {
      defaultValue = filterButtonsOptions[0];
    }

    return (
      <Select
        className="react-select primary"
        classNamePrefix="react-select"
        name="filterButton"
        defaultValue={defaultValue}
        onChange={(option) => {
          setParams(option.value.value);
          setForceRefreshCount((prevValue) => prevValue + 1);
        }}
        isHidden="true"
        options={filterButtonsOptions.map((option) => ({
          value: {
            ...option,
            toString: () => `${option.value}`,
          },
          label: `${option.label}`,
        }))}
      />
    );
  });

  const columns = useMemo(
    () => [
      {
        Header: "User",
        accessor: "username",
        sortBy: "username",
        flex: "1 1 0",
      },
      {
        Header: "IP Address",
        accessor: "ipAddress",
        sortBy: "ipAddress",
        flex: "1 1 0",
      },
      {
        Header: "Type",
        accessor: "type",
        sortBy: "type",
        flex: "1 1 0",
      },
      {
        Header: "Description",
        accessor: "description",
        sortBy: "description",
        flex: "1 1 0",
      },
      {
        Header: "Status",
        accessor: "status",
        sortBy: "status",
        flex: "1 1 0",
      },
      {
        Header: "Date",
        accessor: "date",
        sortBy: "date",
        Cell: (props) => {
          return (
            <div>
              {props.settings.general_convert_date_with_user_timezone === "true"
                ? Moment(props.value)
                    .tz(props.settings.user_timezone)
                    .format("yy-MM-DD hh:mm:ss a")
                : Moment.utc(props.value).format("yy-MM-DD hh:mm:ss a")}
            </div>
          );
        },
        flex: "1 1 0",
      },
      {
        Header: "",
        accessor: "detail",
        Cell: (props) => {
          if (props.value) {
            return (
              <UncontrolledDropdown direction="down">
                <DropdownToggle
                  caret
                  color="info"
                  style={{
                    padding: "6px 14px 6px 14px",
                  }}
                >
                  Actions
                </DropdownToggle>
                <DropdownMenu container={"body"}>
                  <DropdownItem
                    onClick={() =>
                      setAlert(
                        <ReactBSAlert
                          style={{ display: "block", marginTop: "-100px" }}
                          onConfirm={() => hideAlert()}
                          onCancel={() => hideAlert()}
                          confirmBtnBsStyle="info"
                          btnSize=""
                        >
                          <div
                            style={{
                              maxHeight: "60vh",
                              overflowY: "auto",
                              textAlign: "left",
                            }}
                          >
                            <DetailsContent value={props.value} />
                          </div>
                        </ReactBSAlert>,
                      )
                    }
                  >
                    Show Details
                  </DropdownItem>
                </DropdownMenu>
              </UncontrolledDropdown>
            );
          } else {
            return "";
          }
        },
        flex: "1 1 0",
        padding: "0px",
        sortable: false,
        filterable: false,
      },
    ],
    [],
  );

  const DetailsContent = ({ value }) => {
    const parsed = tryParseJson(value);

    if (!parsed) {
      return renderValue(value);
    }

    const isFlatObject =
      !Array.isArray(parsed) &&
      Object.values(parsed).every((v) => typeof v !== "object" || v === null);

    if (isFlatObject) {
      return (
        <table style={{ width: "100%", textAlign: "left" }}>
          <tbody>
            {Object.entries(parsed).map(([key, val]) => (
              <tr key={key}>
                <td
                  style={{
                    fontWeight: 600,
                    paddingRight: 16,
                    verticalAlign: "top",
                    whiteSpace: "nowrap",
                  }}
                >
                  {key}
                </td>
                <td style={{ wordBreak: "break-word" }}>{String(val)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }

    return renderValue(JSON.stringify(parsed, null, 2));
  };

  const tryParseJson = (value) => {
    if (typeof value !== "string") return null;
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === "object" ? parsed : null;
    } catch {
      return null;
    }
  };

  const renderValue = (value) => (
    <pre
      style={{
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        margin: 0,
      }}
    >
      {value}
    </pre>
  );
  return (
    <>
      <ToastContainer />
      <div className="content">
        {alert}
        <LoadingOverlay
          active={showLoading}
          spinner
          text="Loading"
          className={showLoading ? "loading" : ""}
        />
        <Row>
          <Col md="12">
            <Card>
              <CardBody>
                <Breadcrumb>
                  <BreadcrumbItem>
                    <NavLink
                      to={{
                        pathname: "/admin/dashboard",
                      }}
                    >
                      <FontAwesomeIcon icon={faHome} />
                    </NavLink>
                  </BreadcrumbItem>
                  <BreadcrumbItem>Auditing</BreadcrumbItem>
                </Breadcrumb>
              </CardBody>
            </Card>
            <Card>
              <CardBody>
                <ReactTable
                  query="/api/v1/audit/list"
                  columns={columns}
                  param={params}
                  title="Auditing"
                  filterButtons={filterButtons}
                  forceRefresh={forceRefreshCount}
                  className="-striped -highlight primary-pagination"
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default Auditing;
